import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, MessageSquareIcon, SendIcon } from 'lucide-react';
export function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    alert('Thank you for contacting us! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };
  return <HelmetProvider data-id="element-6789" data-mp="27r7yf8cz9i5i">
      <SEO title="Contact Us" description="Get in touch with Innova Pharmacy. Contact our customer support team for assistance with orders, prescriptions, or any questions." canonical="/contact-us" data-id="element-6790" data-mp="27qq6up19fs3x" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6791" data-mp="2hmjg9zjon5hh">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6792" data-mp="2mlcq9hzrgecw">
          <AnnouncementBar data-id="element-6793" data-mp="1wyiiswi3orw1" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6794" data-mp="1wztjuok5vljg" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={true} data-id="element-6795" data-mp="26pmwztzyc8pt" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-6796" data-mp="2aqqbac2cpwox">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6797" data-mp="2pmo5hospsqyi">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-6798" data-mp="2kocq6yjnho8w" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Contact Us'
        }]} title="Contact Us" description="Have a question or need assistance? Our team is here to help you 24/7. Reach out to us through any of the channels below." data-id="element-6799" data-mp="qcxzofitfks3" />
          <div className="container mx-auto px-4 py-12" data-id="element-6800" data-mp="2j7yyk73caj5f">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-6801" data-mp="2j6fhwfdru634">
              {/* Contact Form */}
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-200" data-id="element-6802" data-mp="2nan7ees1zq2e">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-6803" data-mp="29bdtjlrbl81l">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-6804" data-mp="7i6fsgrcuyht">
                  <div data-id="element-6805" data-mp="7izcsq0ii9mr">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6806" data-mp="ch9ab79plmbj">
                      Full Name *
                    </label>
                    <input type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="Enter your full name" data-id="element-6807" data-mp="28j4bul04e0q0" />
                  </div>
                  <div data-id="element-6808" data-mp="1fw9qzmze4zc0">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6809" data-mp="1ftdv0473skq7">
                      Email Address *
                    </label>
                    <input type="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="your.email@example.com" data-id="element-6810" data-mp="16u0l5af2hu4y" />
                  </div>
                  <div data-id="element-6811" data-mp="2uue43rinl6fb">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6812" data-mp="2uqzy89as6f34">
                      Phone Number *
                    </label>
                    <input type="tel" required value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="+966 50 123 4567" data-id="element-6813" data-mp="33sg9ej9f6l8e" />
                  </div>
                  <div data-id="element-6814" data-mp="3t3cdkl8dkjg8">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6815" data-mp="3t6qcfjhtyaix">
                      Subject *
                    </label>
                    <select required value={formData.subject} onChange={e => setFormData({
                    ...formData,
                    subject: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-6816" data-mp="30f6oxne7aab4">
                      <option value="" data-id="element-6817" data-mp="knslhpjfx72r">Select a subject</option>
                      <option value="general" data-id="element-6818" data-mp="s8cz1jpb0fjg">General Inquiry</option>
                      <option value="order" data-id="element-6819" data-mp="s71v9jvuz7sc">Order Issue</option>
                      <option value="prescription" data-id="element-6820" data-mp="x8hfxau4jca7">
                        Prescription Question
                      </option>
                      <option value="complaint" data-id="element-6821" data-mp="ic06wq9eq9qt">Complaint</option>
                      <option value="suggestion" data-id="element-6822" data-mp="n8gwzq062y6j">Suggestion</option>
                    </select>
                  </div>
                  <div data-id="element-6823" data-mp="1b05udkruicuc">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6824" data-mp="1b0xn8nu25wgx">
                      Message *
                    </label>
                    <textarea required value={formData.message} onChange={e => setFormData({
                    ...formData,
                    message: e.target.value
                  })} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent resize-none" placeholder="How can we help you?" data-id="element-6825" data-mp="119t9frg4chno" />
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full bg-[#1E2B55] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#162041] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-6826" data-mp="3lzrq57oq97hf">
                    {isSubmitting ? 'Sending...' : <>
                        <SendIcon className="w-5 h-5" data-id="element-6827" data-mp="3m25ikzvm1br" />
                        Send Message
                      </>}
                  </button>
                </form>
              </div>
              {/* Contact Information */}
              <div className="space-y-6" data-id="element-6828" data-mp="2odkpg8vjnzac">
                {/* Contact Cards */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200" data-id="element-6829" data-mp="2e946v7qok42u">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-6830" data-mp="2j75rvy7o2dgy">
                    Get In Touch
                  </h2>
                  <div className="space-y-4" data-id="element-6831" data-mp="2o3w6si7qj30s">
                    <a href="tel:+966920000000" className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6832" data-mp="2swpffh6bqvi5">
                      <div className="bg-blue-100 p-3 rounded-full" data-id="element-6833" data-mp="31w4183bjqhwb">
                        <PhoneIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-6834" data-mp="36whycqbm07fk" />
                      </div>
                      <div data-id="element-6835" data-mp="3bv1mmxdodvb3">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-6836" data-mp="3bua1kghpffz2">
                          Phone Number
                        </h3>
                        <p className="text-gray-600" data-id="element-6837" data-mp="1cka9nupvsms8">+966 920 000 000</p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-6838" data-mp="1hi3funr6pgsi">
                          Available 24/7
                        </p>
                      </div>
                    </a>
                    <a href="mailto:support@innovapharmacy.com" className="flex items-start gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6839" data-mp="313hn2vh0vg24">
                      <div className="bg-blue-100 p-3 rounded-full" data-id="element-6840" data-mp="3a3xrq89i4t8k">
                        <MailIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-6841" data-mp="3f2r0xohr9y2x" />
                      </div>
                      <div data-id="element-6842" data-mp="3k0sp2nu1gnl9">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-6843" data-mp="3k00yjrfatg1u">Email</h3>
                        <p className="text-gray-600" data-id="element-6844" data-mp="3orsl7qvc31mp">
                          support@innovapharmacy.com
                        </p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-6845" data-mp="3ssui411s31li">
                          Response within 24 hours
                        </p>
                      </div>
                    </a>
                    <div className="flex items-start gap-4 p-4 rounded-lg" data-id="element-6846" data-mp="6mp9hnav3rdb">
                      <div className="bg-blue-100 p-3 rounded-full" data-id="element-6847" data-mp="gre46e40ynuy">
                        <ClockIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-6848" data-mp="bqqcdq7z22zo" />
                      </div>
                      <div data-id="element-6849" data-mp="6rn5s1ccfopl">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-6850" data-mp="6sy9601vmug6">
                          Working Hours
                        </h3>
                        <p className="text-gray-600" data-id="element-6851" data-mp="2sxvwiqikpap">24/7 Service</p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-6852" data-mp="zgcaeaclyc63">
                          Always here to help
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-lg" data-id="element-6853" data-mp="pppzgtfu69tq">
                      <div className="bg-blue-100 p-3 rounded-full" data-id="element-6854" data-mp="lq8v63c5biq5">
                        <MapPinIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-6855" data-mp="1i7bryqhrwjpc" />
                      </div>
                      <div data-id="element-6856" data-mp="1dgn3zl5ay4uy">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-6857" data-mp="1dgd9bk9bb9ix">
                          Location
                        </h3>
                        <p className="text-gray-600" data-id="element-6858" data-mp="37oz6xtc4ar5r">Riyadh, Saudi Arabia</p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-6859" data-mp="3cmin4ysdle0c">
                          Multiple branches across KSA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Quick Links */}
                <div className="bg-gradient-to-br from-[#1E2B55] to-[#2a3a6f] rounded-xl shadow-sm p-6 text-white" data-id="element-6860" data-mp="2oxpzdplwqcgt">
                  <h3 className="text-xl font-bold mb-4" data-id="element-6861" data-mp="2ty400ml6ik4t">Need Quick Help?</h3>
                  <div className="space-y-3" data-id="element-6862" data-mp="2f0bgzkgfbdlv">
                    <a href="/faq" className="flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-colors" data-id="element-6863" data-mp="2f1mkdj5yijcg">
                      <span className="font-medium" data-id="element-6864" data-mp="i7jgz84nkdkl">FAQ</span>
                      <MessageSquareIcon className="w-5 h-5" data-id="element-6865" data-mp="i6zy9p76hl5i" />
                    </a>
                    <a href="/prescription" className="flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-colors" data-id="element-6866" data-mp="22qc4kkmyro5a">
                      <span className="font-medium" data-id="element-6867" data-mp="2chgkpnodlf1p">Upload Prescription</span>
                      <MessageSquareIcon className="w-5 h-5" data-id="element-6868" data-mp="27n2kn7yrqjqp" />
                    </a>
                    <button className="w-full flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition-colors" data-id="element-6869" data-mp="2lequf4ljow4i">
                      <span className="font-medium" data-id="element-6870" data-mp="2vdzkvnh7bsm4">Chat with Pharmacist</span>
                      <MessageSquareIcon className="w-5 h-5" data-id="element-6871" data-mp="2vbvjbc6rz5p0" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer data-id="element-6872" data-mp="1121nqjw62n6l" />
        <MobileFooterNav data-id="element-6873" data-mp="w1dlt36eglxl" />
        <PharmacistChat data-id="element-6874" data-mp="w1x1sechp08z" />
        <div className="h-16 md:hidden" data-id="element-6875" data-mp="w2or57fhu1qt"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6876" data-mp="xijazazvbytz" />
      </div>
    </HelmetProvider>;
}